import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { FaPlus, FaSearch, FaList, FaEye, FaFilter } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/maintenanceListing";

const VehicleTypeMaintenanceListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  // ✅ localStorage values (same style as your attached code)
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ddl data
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [vehicleSubTypes, setVehicleSubTypes] = useState([]);

  // filters
  const [vehicletypeid, setVehicletypeid] = useState("Select");
  const [vehiclesubtypeid, setVehiclesubtypeid] = useState("Select");
  const [showFilters, setShowFilters] = useState(true);

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rights, setRights] = useState({ canAccess: true, canAdd: true });

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const normalizeId = (val) => {
    if (!val || val === "Select") return "%";
    return String(val);
  };

  const normalizeRow = (row) => {
    return {
      ...row,
      vehicleType:
        row?.VehicleType ||
        row?.Vehicletypename ||
        row?.fld_vehicletypename ||
        row?.vehicleType ||
        row?.vehicletypename ||
        row?.["Vehicle Type"] ||
        "-",
      vehicleSubType:
        row?.VehicleSubType ||
        row?.Vehicle_subtypename ||
        row?.fld_vehicle_subtypename ||
        row?.vehiclesubtype ||
        row?.vehicleSubType ||
        row?.["Vehicle Sub Type"] ||
        "-",
      vehicleAndSubName:
        row?.VehicleandSubname ||
        row?.VehicleAndSubName ||
        row?.fld_vehicleandsubname ||
        row?.vehicleAndSubName ||
        row?.VehicleSubTypeName ||
        row?.VehicleSubType ||
        "",
      vehicleTypeId:
        row?.fld_vehicletypeid ||
        row?.VehicleTypeId ||
        row?.vehicletypeid ||
        row?.id ||
        0,
      vehicleSubTypeId:
        row?.fld_vehicle_subtypeid ||
        row?.VehicleSubTypeId ||
        row?.vehiclesubtypeid ||
        0,
    };
  };

  // ---------- Load DDL ----------
  const loadDDL = async () => {
    try {
      const r = await axios.get(`${API_BASE}/ddl`, {
        headers: commonHeaders,
      });

      if (!r.data?.success) {
        throw new Error(r.data?.message || "DDL failed");
      }

      setVehicleTypes(Array.isArray(r.data?.data?.vehicleTypes) ? r.data.data.vehicleTypes : []);
      setVehicleSubTypes(
        Array.isArray(r.data?.data?.vehicleSubTypes) ? r.data.data.vehicleSubTypes : []
      );
    } catch (err) {
      console.error(err);
      toast.error("Failed to load dropdown data!");
      setVehicleTypes([]);
      setVehicleSubTypes([]);
    }
  };

  // ---------- List/Search ----------
  const loadList = async () => {
    if (!branchId) {
      toast.error("branchId missing in localStorage!");
      return;
    }

    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/list`, {
        params: {
          branchId,
          vehicletypeid: normalizeId(vehicletypeid),
          vehiclesubtypeid: normalizeId(vehiclesubtypeid),
        },
        headers: commonHeaders,
      });

      const data = Array.isArray(r.data?.data) ? r.data.data : [];
      setRows(data.map(normalizeRow));
    } catch (err) {
      console.error(err);
      toast.error("Failed to load list");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadViewAll = async () => {
    if (!branchId) {
      toast.error("branchId missing in localStorage!");
      return;
    }

    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/viewall`, {
        params: {
          branchId,
          vehicletypeid: normalizeId(vehicletypeid),
          vehiclesubtypeid: normalizeId(vehiclesubtypeid),
        },
        headers: commonHeaders,
      });

      const data = Array.isArray(r.data?.data) ? r.data.data : [];
      setRows(data.map(normalizeRow));
    } catch (err) {
      console.error(err);
      toast.error("Failed to load view all list");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Vehicle type change ----------
  const onVehicleTypeChange = async (e) => {
    const val = e.target.value;
    setVehicletypeid(val);
    setVehiclesubtypeid("Select");

    if (val === "Select" || !val) {
      await loadDDL();
      return;
    }

    try {
      const r = await axios.get(`${API_BASE}/subtypes/${val}`, {
        headers: commonHeaders,
      });

      if (r.data?.success) {
        setVehicleSubTypes(Array.isArray(r.data?.data) ? r.data.data : []);
      } else {
        setVehicleSubTypes([]);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load subtypes");
      setVehicleSubTypes([]);
    }
  };

  // ---------- Row actions ----------
  const onEdit = (row) => {
    sessionStorage.setItem("VehicleandSubname", row.vehicleAndSubName || "");
    sessionStorage.setItem("vehicletypeid", String(row.vehicleTypeId || ""));
    sessionStorage.setItem("vehiclesubtypeid", String(row.vehicleSubTypeId || ""));

    navigate("/vehicle/VehicleTypeMaintenanceEntry");
  };

  const onAddSubAssembly = (row) => {
    sessionStorage.setItem("vehicletypeid", String(row.vehicleTypeId || ""));
    sessionStorage.setItem("vehiclesubtypeid", String(row.vehicleSubTypeId || ""));

    navigate("/vehicle/SubAssemblyListing");
  };

  // ---------- Initial load ----------
  useEffect(() => {
    (async () => {
      try {
        await loadDDL();
        await loadList();
      } catch (err) {
        console.error(err);
        toast.error("Failed to initialize page");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  if (!rights.canAccess) {
    return (
      <VehicleLayoutWrapper>
        <div className="p-6">
          <div className="p-4 rounded-lg border border-red-300 bg-red-50 text-red-700">
            You don’t have rights to access this page.
          </div>
        </div>
      </VehicleLayoutWrapper>
    );
  }

  return (
    <VehicleLayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">VEHICLE TYPE MAINTENANCE</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-4">
            <h2 className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Vehicle Type Maintenance Listing
            </h2>

            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
              }`}
            >
              {rows.length} Records
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800" +
                    (showFilters ? " bg-gray-800" : "")
                  : "border-gray-300 text-gray-700 hover:bg-gray-50" +
                    (showFilters ? " bg-gray-50" : "")
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            {rights.canAdd && (
              <button
                onClick={() => {
                  sessionStorage.setItem("VehicleandSubname", "");
                  sessionStorage.setItem("vehicletypeid", "");
                  sessionStorage.setItem("vehiclesubtypeid", "");
                  navigate("/vehicle/VehicleTypeMaintenanceEntry");
                }}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
              >
                <FaPlus className="w-4 h-4" />
                Add New
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div
            className={`rounded-xl p-5 border mb-6 ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Vehicle Type */}
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Vehicle Type
                </label>
                <select
                  value={vehicletypeid}
                  onChange={onVehicleTypeChange}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="Select">Select</option>
                  {vehicleTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Vehicle Subtype */}
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Vehicle Sub Type
                </label>
                <select
                  value={vehiclesubtypeid}
                  onChange={(e) => setVehiclesubtypeid(e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="Select">Select</option>
                  {vehicleSubTypes.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Buttons */}
              <div className="flex items-end gap-3">
                <button
                  onClick={loadList}
                  className={`flex items-center gap-2 px-4 py-2 rounded text-white ${buttonClass} ${hoverClass}`}
                >
                  <FaSearch /> Search
                </button>

                <button
                  onClick={loadViewAll}
                  className="flex items-center gap-2 px-4 py-2 rounded border border-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <FaList /> View All
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div
          className={`rounded-xl shadow-sm border overflow-hidden ${
            isDark ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className={`${buttonClass} text-white`}>
                <tr>
                  <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">S.No</th>
                  <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">
                    Vehicle Type
                  </th>
                  <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">
                    Vehicle Sub Type
                  </th>
                  <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Action</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <tr key={idx} className={isDark ? "bg-gray-800" : "bg-white"}>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-10" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-40" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-40" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-24" />
                      </td>
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                      No records found.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, idx) => (
                    <tr
                      key={idx}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 ${
                        isDark ? "bg-gray-900" : "bg-white"
                      }`}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {idx + 1}
                      </td>

                      <td className="px-4 py-3">
                        <span className="text-blue-600 dark:text-blue-400 font-medium">
                          {row.vehicleType}
                        </span>
                      </td>

                      <td className="px-4 py-3">{row.vehicleSubType}</td>

                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => onEdit(row)}
                            className={`px-3 py-1 rounded text-xs font-medium ${
                              isDark
                                ? "bg-blue-600 hover:bg-blue-700 text-white"
                                : "bg-blue-100 hover:bg-blue-200 text-blue-700"
                            }`}
                          >
                            <FaEye className="inline mr-1" />
                            Edit
                          </button>

                          {/* Uncomment if needed */}
                          {/* <button
                            onClick={() => onAddSubAssembly(row)}
                            className={`px-3 py-1 rounded text-xs font-medium ${
                              isDark
                                ? "bg-green-600 hover:bg-green-700 text-white"
                                : "bg-green-100 hover:bg-green-200 text-green-700"
                            }`}
                          >
                            + SubAssembly
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleTypeMaintenanceListing;